import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isBoolean } from 'lodash-es';
import Card from 'reactstrap/lib/Card';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import {
  LEAD_FORM_EXPLAINING_TEXT,
  LEAD_FORM_HEADING,
  PRICE_CHECKER_CREATIVE_ID,
} from 'site-modules/shared/constants/price-checker';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { generateNewFlatUrl } from 'site-modules/shared/utils/srp-link-constructor';
import { TransparentPricingLeadForm } from 'site-modules/shared/components/lead-form/unified-lead-form-pages/transparent-pricing-lead-form';
import { Link } from 'site-modules/shared/components/link/link';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';

import './price-checker-embedded-lead-form.scss';

const FOR_SALE_CTA = 'See all for sale';

function PriceCheckerEmbeddedLeadFormUI({
  cardClassName = null,
  forSaleClassName = null,
  leadFormParams = null,
  leadFormOptions = null,
  leadFormDealerCount = null,
  leadFormMinHeightType = null,
  hasLeadFormDealers = false,
  visitorLocation = undefined,
  isMobile = false,
  isCore6830Enabled = false,
}) {
  const { zipCode, city } = visitorLocation;
  const isCore6830International = isCore6830Enabled && isUnknownZip(visitorLocation);
  const hasVehicle = !!get(leadFormParams, 'vehicle');
  const hasLeadForm = hasLeadFormDealers || !!leadFormDealerCount;

  useEffect(() => {
    if (hasVehicle && !hasLeadForm) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'lead_form_fallback',
          creative_id: PRICE_CHECKER_CREATIVE_ID,
          value: FOR_SALE_CTA,
        },
      });
    }
  }, [hasLeadForm, hasVehicle]);

  if (!hasVehicle) {
    return null;
  }

  return hasLeadForm ? (
    <Card className={cardClassName}>
      <MinHeightWrapper componentType={leadFormMinHeightType}>
        <TransparentPricingLeadForm
          params={{ ...leadFormParams, zipCode, city }}
          options={{
            isMobile,
            creativeId: CREATIVE_ID.NEW_EMBEDDED_PRICE_CHECKER,
            utmCampaign: 'new-embedded-price_checker',
            headingText: LEAD_FORM_HEADING,
            explainingTextMobile: LEAD_FORM_EXPLAINING_TEXT,
            explainingTextDesktop: LEAD_FORM_EXPLAINING_TEXT,
            hideZipForm: true,
            ...leadFormOptions,
          }}
        />
      </MinHeightWrapper>
    </Card>
  ) : (
    <div className={classnames('for-sale-wrapper', forSaleClassName)}>
      <Button
        tag={Link}
        color="primary-b"
        size="lg"
        className={classnames('for-sale-link text-transform-none text-white px-3_5 py-0_75', {
          'w-100': isMobile,
        })}
        to={`${generateNewFlatUrl({
          make: get(leadFormParams, 'vehicle.make.slug'),
          model: get(leadFormParams, 'vehicle.model.slug'),
        })}?${isCore6830International ? 'national=true' : 'radius=200'}`}
        data-tracking-id="view_search_result"
      >
        {FOR_SALE_CTA}
      </Button>
    </div>
  );
}

PriceCheckerEmbeddedLeadFormUI.propTypes = {
  cardClassName: PropTypes.string,
  forSaleClassName: PropTypes.string,
  leadFormParams: PropTypes.shape({
    srpLink: PropTypes.string,
    vehicle: PropTypes.shape({
      makeName: PropTypes.string,
      modelName: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      trimName: PropTypes.string,
      id: PropTypes.number,
      make: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
      model: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
      submodels: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
    }),
    styleId: PropTypes.number,
    trim: PropTypes.string,
  }),
  leadFormOptions: PropTypes.shape({}),
  leadFormDealerCount: PropTypes.number,
  leadFormMinHeightType: PropTypes.string,
  hasLeadFormDealers: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
  isCore6830Enabled: PropTypes.bool,
};

export const PriceCheckerEmbeddedLeadForm = connectToModel(PriceCheckerEmbeddedLeadFormUI, {
  visitorLocation: bindToPath('location', VisitorModel),
  leadFormDealerCount: bindToPath(
    ({ leadFormParams, hasLeadFormDealers }) =>
      !isBoolean(hasLeadFormDealers) &&
      get(leadFormParams, 'vehicle.make.slug') &&
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug: makeNiceName(leadFormParams.vehicle.make.slug),
        modelSlug: makeNiceName(leadFormParams.vehicle.model.slug),
        strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
      }),
    UnifiedLeadFormModel
  ),
});
